/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { OpenAPIHousien } from '@/services/requests/core/OpenAPI';
import { request as __request } from '@/services/requests/core/request';
import type { CancelablePromise } from '@/services/requests/core/CancelablePromise';
import type {
  ClientSuggestableField,
  EmployerPrefillResponse,
  FieldValuesResponse,
} from '@/services/requests/generated/types.gen';

/** How many suggestions the form asks for. */
export const FIELD_VALUES_LIMIT = 10;

/**
 * Values the agency already stored for one free-text client field.
 *
 * @param agencyId - The agency whose clients are read.
 * @param field - One of the closed list of suggestable fields.
 * @param query - What the broker typed (two letters or more).
 * @returns The matching values, most used first.
 */
export function fetchClientFieldValues(
  agencyId: string,
  field: ClientSuggestableField,
  query: string,
): CancelablePromise<FieldValuesResponse> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/clients/field-values',
    path: { agencyId },
    query: { field, q: query, limit: FIELD_VALUES_LIMIT },
    errors: { 400: 'UNKNOWN_FIELD', 403: 'FORBIDDEN' },
  });
}

/**
 * The industry most used by the agency's clients at one employer.
 *
 * @param agencyId - The agency whose clients are read.
 * @param name - The employer the broker picked.
 * @returns The suggested values; empty when nothing qualifies.
 */
export function fetchEmployerPrefill(
  agencyId: string,
  name: string,
): CancelablePromise<EmployerPrefillResponse> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/clients/employer-prefill',
    path: { agencyId },
    query: { name },
    errors: { 400: 'MISSING_NAME', 403: 'FORBIDDEN' },
  });
}
